import React, { useEffect, useState } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { NavigationProvider } from './context/NavigationContext';
import CountdownScreen from './screens/CountdownScreen';
import PasswordScreen from './screens/PasswordScreen';
import HomeScreen from './screens/HomeScreen';
import { Heart } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

function App() {
  const [currentScreen, setCurrentScreen] = useState<'countdown' | 'password' | 'home'>('countdown');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading for stunning effect
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleCountdownComplete = () => {
    setCurrentScreen('password');
  };

  const handlePasswordSuccess = () => {
    setCurrentScreen('home');
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen w-screen justify-center items-center bg-romantic-light dark:bg-romantic-dark transition-colors duration-500">
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="text-primary-red mb-4"
        >
          <Heart size={64} fill="currentColor" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-gray-700 dark:text-gray-300 font-poppins font-medium"
        >
          Preparing some love surprises... ❤️
        </motion.p>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <div className="min-h-screen bg-romantic-light dark:bg-romantic-dark transition-colors duration-500 selection:bg-primary-red/30">
          <AnimatePresence mode="wait">
            {currentScreen === 'countdown' && (
              <motion.div
                key="countdown"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <CountdownScreen onComplete={handleCountdownComplete} />
              </motion.div>
            )}

            {currentScreen === 'password' && (
              <motion.div
                key="password"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                transition={{ duration: 0.5 }}
              >
                <PasswordScreen onSuccess={handlePasswordSuccess} />
              </motion.div>
            )}

            {currentScreen === 'home' && (
              <motion.div
                key="home"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <NavigationProvider>
                  <HomeScreen />
                </NavigationProvider>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
